import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { notifyDiscussionReply } from "@/lib/notifyDiscussionReply";

export interface Discussion {
  id: string;
  course_id: string;
  module_id: string | null;
  user_id: string;
  title: string;
  content: string;
  image_url: string | null;
  link_url: string | null;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
  reply_count?: number;
}

export interface DiscussionReply {
  id: string;
  discussion_id: string;
  user_id: string;
  content: string;
  image_url: string | null;
  link_url: string | null;
  created_at: string;
  updated_at: string;
  author?: {
    full_name: string;
    avatar_url: string | null;
  };
}

export function useDiscussions(courseId: string, moduleId?: string | null) {
  return useQuery({
    queryKey: ["discussions", courseId, moduleId],
    queryFn: async () => {
      let query = supabase
        .from("course_discussions")
        .select("*")
        .eq("course_id", courseId)
        .order("is_pinned", { ascending: false })
        .order("created_at", { ascending: false });

      if (moduleId) {
        query = query.eq("module_id", moduleId);
      }

      const { data, error } = await query;

      if (error) throw error;
      if (!data || data.length === 0) return [];

      const userIds = [...new Set(data.map((d) => d.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const profileMap = profiles?.reduce((acc, p) => {
        acc[p.id] = { full_name: p.full_name, avatar_url: p.avatar_url };
        return acc;
      }, {} as Record<string, { full_name: string; avatar_url: string | null }>);

      const discussionIds = data.map((d) => d.id);
      const { data: replyCounts } = await supabase
        .from("discussion_replies")
        .select("discussion_id")
        .in("discussion_id", discussionIds);

      const counts = replyCounts?.reduce((acc, r) => {
        acc[r.discussion_id] = (acc[r.discussion_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return data.map((d) => ({
        ...d,
        author: profileMap?.[d.user_id],
        reply_count: counts?.[d.id] || 0,
      })) as Discussion[];
    },
    enabled: !!courseId,
  });
}

export function useDiscussion(discussionId: string) {
  return useQuery({
    queryKey: ["discussion", discussionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_discussions")
        .select("*")
        .eq("id", discussionId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name, avatar_url")
        .eq("id", data.user_id)
        .maybeSingle();

      return {
        ...data,
        author: profile,
      } as Discussion;
    },
    enabled: !!discussionId,
  });
}

export function useDiscussionReplies(discussionId: string) {
  return useQuery({
    queryKey: ["discussion-replies", discussionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("discussion_replies")
        .select("*")
        .eq("discussion_id", discussionId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) return [];

      const userIds = [...new Set(data.map((r) => r.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", userIds);

      const profileMap = profiles?.reduce((acc, p) => {
        acc[p.id] = { full_name: p.full_name, avatar_url: p.avatar_url };
        return acc;
      }, {} as Record<string, { full_name: string; avatar_url: string | null }>);

      return data.map((r) => ({
        ...r,
        author: profileMap?.[r.user_id],
      })) as DiscussionReply[];
    },
    enabled: !!discussionId,
  });
}

export function useCreateDiscussion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      courseId,
      moduleId,
      title,
      content,
      image_url,
      link_url,
    }: {
      courseId: string;
      moduleId?: string | null;
      title: string;
      content: string;
      image_url?: string;
      link_url?: string;
    }) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("course_discussions")
        .insert({
          course_id: courseId,
          module_id: moduleId || null,
          user_id: user.user.id,
          title,
          content,
          image_url: image_url || null,
          link_url: link_url || null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["discussions", variables.courseId],
      });
    },
  });
}

export function useCreateReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      discussionId,
      content,
      image_url,
      link_url,
      discussionTitle,
    }: {
      discussionId: string;
      content: string;
      image_url?: string;
      link_url?: string;
      discussionTitle?: string;
    }) => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("discussion_replies")
        .insert({
          discussion_id: discussionId,
          user_id: user.user.id,
          content,
          image_url: image_url || null,
          link_url: link_url || null,
        })
        .select()
        .single();

      if (error) throw error;

      if (discussionTitle) {
        notifyDiscussionReply({
          discussionId,
          discussionType: "course",
          replyContent: content,
          replyAuthorId: user.user.id,
          discussionTitle,
        });
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["discussion-replies", variables.discussionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["discussions"],
      });
    },
  });
}

export function useUpdateDiscussion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      title,
      content,
      image_url,
      link_url,
    }: {
      id: string;
      title?: string;
      content: string;
      image_url?: string | null;
      link_url?: string | null;
    }) => {
      const { error } = await supabase
        .from("course_discussions")
        .update({
          ...(title !== undefined ? { title } : {}),
          content,
          image_url: image_url ?? null,
          link_url: link_url ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["discussion", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["discussions"] });
    },
  });
}

export function useUpdateReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      content,
      image_url,
      link_url,
      discussionId,
    }: {
      id: string;
      content: string;
      image_url?: string | null;
      link_url?: string | null;
      discussionId: string;
    }) => {
      const { error } = await supabase
        .from("discussion_replies")
        .update({
          content,
          image_url: image_url ?? null,
          link_url: link_url ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["discussion-replies", variables.discussionId],
      });
    },
  });
}

export function useDeleteDiscussion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, courseId }: { id: string; courseId?: string }) => {
      const { error } = await supabase
        .from("course_discussions")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["discussions"] });
      if (variables.courseId) {
        queryClient.invalidateQueries({ queryKey: ["discussions", variables.courseId] });
      }
    },
  });
}

export function useDeleteReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (replyId: string) => {
      const { error } = await supabase
        .from("discussion_replies")
        .delete()
        .eq("id", replyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["discussion-replies"] });
      queryClient.invalidateQueries({ queryKey: ["discussions"] });
    },
  });
}
